import { Button, ButtonRow, LinkButton } from '@timelyapp/react';
import classNames from 'classnames';
import ErrorBoundary from 'components/error-boundary';
import { InfoIcon } from 'components/Icons';
import PrivacyIndicator from 'components/PrivacyIndicator';
import Popover from 'components/popovers/Popover';
import Surface from 'components/Surface';
import React, { Suspense, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { isLazy } from 'react-is';
import { preloadIfLazy, registerLazy, useLazyStatus } from 'utils/react';
import { useIsMobile } from 'utils/viewport';
import styles from './ContextualTooltip.module.scss';

const LazyTimesheet = registerLazy(() => import('./Timesheet'));
const LazyTimeline = registerLazy(() => import('./Timeline'));
const LazyList = registerLazy(() => import('./List'));

interface ContextualTooltipProps {
  title: string;

  description: string;

  /** If true, indicates that the content is visible only to you. */
  contextIsPrivate?: boolean;

  learnMoreUrl: string;

  isInitiallyOpen?: boolean;

  testId?: string;

  children?: React.ReactNode;
}

const ContextualTooltipContent: React.FC<{
  children?: React.ReactNode;
  contextIsPrivate?: boolean;
  description: string;
  learnMoreUrl: string;
  title: string;
  lazyStatus: number | null;
  onClose: () => void;
}> = ({ children, contextIsPrivate, description, learnMoreUrl, title, lazyStatus, onClose }) => (
  <>
    <h1>{title}</h1>
    {!!children &&
      (isLazy(children) ? (
        <Surface
          surface={2}
          className={classNames(styles.content, styles.lazy, {
            [styles.lazyInitialized]: lazyStatus === 1,
          })}
        >
          <ErrorBoundary FallbackComponent={null}>
            <Suspense fallback={null}>{children}</Suspense>
          </ErrorBoundary>
        </Surface>
      ) : (
        <Surface surface={2} className={styles.content}>
          {children}
        </Surface>
      ))}
    <PrivacyIndicator isPublic={!contextIsPrivate} element="p" className={styles.privacy} />
    <p>{description}</p>
    <ButtonRow>
      <Button priority="xhigh" onClick={onClose} data-testid="contextual-tooltip-got-it">
        Got it
      </Button>
      <LinkButton
        priority="low"
        linkTo={learnMoreUrl}
        newWindow
        data-testid="contextual-tooltip-learn-more"
      >
        Learn more
      </LinkButton>
    </ButtonRow>
  </>
);

const ContextualTooltip: React.FC<ContextualTooltipProps> & {
  Timesheet: typeof LazyTimesheet;
  Timeline: typeof LazyTimeline;
  List: typeof LazyList;
} = ({ children, contextIsPrivate, description, isInitiallyOpen, learnMoreUrl, testId, title }) => {
  const popoverRef = useRef<Popover>(null);
  const lazyStatus = useLazyStatus(children);
  const isMobile = useIsMobile();
  const [isMobileOpen, setIsMobileOpen] = useState(!!isInitiallyOpen);

  const handleMouseEnter = useCallback(() => {
    preloadIfLazy(children);
  }, [children]);

  const handleMobileOpen = useCallback(() => {
    preloadIfLazy(children);
    setIsMobileOpen(true);
  }, [children]);

  const handleMobileClose = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  if (isMobile) {
    return (
      <>
        <span className={styles.targetWrapper}>
          <InfoIcon
            backgroundColor="currentColor"
            color="currentColor"
            className={styles.icon}
            data-testid="contextual-tooltip-target"
            onClick={handleMobileOpen}
          />
        </span>
        {isMobileOpen &&
          ReactDOM.createPortal(
            <div className={styles.mobileOverlay} onClick={handleMobileClose} data-testid={testId}>
              <Surface
                surface={1}
                className={classNames(styles.mobileCard, {
                  [styles.withContent]: !!children,
                })}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <ContextualTooltipContent
                  contextIsPrivate={contextIsPrivate}
                  description={description}
                  learnMoreUrl={learnMoreUrl}
                  title={title}
                  lazyStatus={lazyStatus}
                  onClose={handleMobileClose}
                >
                  {children}
                </ContextualTooltipContent>
              </Surface>
            </div>,
            document.body
          )}
      </>
    );
  }

  return (
    <Popover
      fixed
      position="auto-start"
      target={
        <InfoIcon
          backgroundColor="currentColor"
          color="currentColor"
          className={styles.icon}
          data-testid="contextual-tooltip-target"
          onMouseEnter={handleMouseEnter}
        />
      }
      targetWrapperElement="span"
      arrow={false}
      isInitiallyOpen={isInitiallyOpen}
      className={classNames(styles.container, {
        [styles.withContent]: !!children,
      })}
      ref={popoverRef}
      testId={testId}
    >
      <ContextualTooltipContent
        contextIsPrivate={contextIsPrivate}
        description={description}
        learnMoreUrl={learnMoreUrl}
        title={title}
        lazyStatus={lazyStatus}
        onClose={() => popoverRef.current?.close()}
      >
        {children}
      </ContextualTooltipContent>
    </Popover>
  );
};

ContextualTooltip.Timesheet = LazyTimesheet;
ContextualTooltip.Timeline = LazyTimeline;
ContextualTooltip.List = LazyList;

export default ContextualTooltip;
